import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Inventory as InventoryIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';

interface MetricCard {
  title: string;
  value: string;
  change: number;
  icon: React.ReactNode;
  color: string;
}

interface TopItem {
  name: string;
  sales: number;
  percentage: number;
  trend: 'up' | 'down';
}

const Dashboard: React.FC = () => {
  const metrics: MetricCard[] = [
    {
      title: 'Today\'s Sales',
      value: '$2,847.63',
      change: 12.5,
      icon: <MoneyIcon />,
      color: '#4caf50'
    },
    {
      title: 'Orders Today',
      value: '156',
      change: 8.2,
      icon: <CartIcon />,
      color: '#2196f3'
    },
    {
      title: 'Customers Served',
      value: '142',
      change: -2.1,
      icon: <PeopleIcon />,
      color: '#ff9800'
    },
    {
      title: 'Avg Order Value',
      value: '$18.25',
      change: 5.7,
      icon: <RestaurantIcon />,
      color: '#9c27b0'
    }
  ];

  const topItems: TopItem[] = [
    { name: 'Birria Rice Bowl', sales: 127, percentage: 23, trend: 'up' },
    { name: 'Chicken Tacos', sales: 98, percentage: 18, trend: 'up' },
    { name: 'Steak Burrito', sales: 89, percentage: 16, trend: 'down' },
    { name: 'Bean Rice Bowl', sales: 67, percentage: 12, trend: 'up' },
    { name: 'Taco Salad', sales: 54, percentage: 10, trend: 'up' }
  ];

  const inventoryAlerts = [
    { item: 'Ground Beef', level: 15, status: 'critical' },
    { item: 'Flour Tortillas', level: 8, status: 'critical' },
    { item: 'Avocados', level: 35, status: 'warning' },
    { item: 'Cheese', level: 45, status: 'normal' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'normal': return 'success';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'critical': return 'Critical';
      case 'warning': return 'Low';
      case 'normal': return 'Good';
      default: return 'Unknown';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Business Dashboard
      </Typography>

      {/* Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        {metrics.map((metric, index) => (
          <Card key={index} sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: metric.color }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {metric.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {metric.change > 0 ? (
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                    ) : (
                      <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={metric.change > 0 ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 'medium' }}
                    >
                      {metric.change > 0 ? '+' : ''}{metric.change}%
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    backgroundColor: `${metric.color}20`,
                    borderRadius: 2,
                    p: 1,
                    color: metric.color
                  }}
                >
                  {metric.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Main Content Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
        {/* Top Selling Items */}
        <Paper sx={{ p: 3, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              Top Selling Items
            </Typography>
            <IconButton size="small">
              <MoreIcon />
            </IconButton>
          </Box>
          
          <List sx={{ p: 0 }}>
            {topItems.map((item, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    #{index + 1}
                  </Typography>
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  secondary={`${item.sales} orders (${item.percentage}%)`}
                />
                <Chip
                  label={item.trend === 'up' ? '↗' : '↘'}
                  size="small"
                  color={item.trend === 'up' ? 'success' : 'error'}
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Inventory Status */}
        <Paper sx={{ p: 3, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              Inventory Status
            </Typography>
            <Button size="small" variant="outlined">
              View All
            </Button>
          </Box>
          
          <List sx={{ p: 0 }}>
            {inventoryAlerts.map((alert, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1 }}>
                <ListItemIcon>
                  <InventoryIcon color={getStatusColor(alert.status) as any} />
                </ListItemIcon>
                <ListItemText
                  primary={alert.item}
                  secondary={`${alert.level}% remaining`}
                />
                <Chip
                  label={getStatusText(alert.status)}
                  size="small"
                  color={getStatusColor(alert.status) as any}
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>

      {/* Bottom Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Sales Chart Placeholder */}
        <Paper sx={{ p: 3, height: 300 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              Sales Trend (Last 7 Days)
            </Typography>
            <Button size="small" variant="outlined">
              View Details
            </Button>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: 'calc(100% - 80px)',
            backgroundColor: 'rgba(66, 66, 66, 0.04)',
            borderRadius: 2
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 64, color: '#424242', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Chart visualization would go here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Integration with Chart.js or Recharts for real-time data visualization
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Quick Actions */}
        <Paper sx={{ p: 3, height: 300 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Quick Actions
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<ScheduleIcon />}
              size="large"
            >
              View Schedule
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              startIcon={<InventoryIcon />}
              size="large"
            >
              Manage Inventory
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PeopleIcon />}
              size="large"
            >
              Staff Management
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RestaurantIcon />}
              size="large"
            >
              Menu Editor
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
